/**
 * Journal entries — list with KPIs, status tabs, detail Sheet, and inline
 * actions (Post / Reverse / Edit if draft).
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BookOpen, Pencil, Plus, RotateCcw, Send } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import {
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ConfirmPopover } from "../../../components/ConfirmPopover";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockAccounts,
  mockJournalEntries,
  mockJournalLines,
  findAccount,
  MOCK_NOW,
  type MockJournalEntry,
  type MockJournalLine,
  type MockJournalStatus,
} from "../../../lib/glMock";
import {
  JournalEntryForm,
  type JournalEntryFormValues,
} from "../../../components/forms/JournalEntryForm";
import { JournalEntryStatusBadge } from "../../../components/gl/JournalEntryStatusBadge";
import { BalanceIndicator } from "../../../components/gl/BalanceIndicator";

export const Route = createFileRoute("/_app/finance/gl")({
  component: GeneralLedgerPage,
});

const TAB_OPTIONS: Array<{
  value: "all" | MockJournalStatus;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "draft", label: "Drafts" },
  { value: "posted", label: "Posted" },
  { value: "reversed", label: "Reversed" },
];

function startOfMonth(now: number): number {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

function startOfFiscalYear(now: number): number {
  const d = new Date(now);
  const m = d.getUTCMonth();
  const y = d.getUTCFullYear();
  // BD fiscal year starts in July
  const fyStart = m >= 6 ? y : y - 1;
  return Date.UTC(fyStart, 6, 1);
}

function GeneralLedgerPage() {
  const isMock = useMockData();
  void isMock;

  const [entries, setEntries] = usePersistentMockState<MockJournalEntry[]>(
    "journalEntries",
    mockJournalEntries,
  );
  const [lines, setLines] = usePersistentMockState<MockJournalLine[]>(
    "journalLines",
    mockJournalLines,
  );
  const [tab, setTab] = useState<"all" | MockJournalStatus>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const monthStart = startOfMonth(MOCK_NOW);
  const fyStart = startOfFiscalYear(MOCK_NOW);

  const kpis = useMemo(() => {
    const postedMtd = entries.filter(
      (e) => e.status === "posted" && e.postedAt && e.postedAt >= monthStart,
    ).length;
    const drafts = entries.filter((e) => e.status === "draft").length;
    const outOfBalanceDrafts = entries.filter((e) => {
      if (e.status !== "draft") return false;
      const t = entryTotalsLocal(lines, e._id);
      return t.debits !== t.credits;
    }).length;
    const postedFy = entries.filter(
      (e) => e.status === "posted" && e.postedAt && e.postedAt >= fyStart,
    ).length;
    return { postedMtd, drafts, outOfBalanceDrafts, postedFy };
  }, [entries, lines, monthStart, fyStart]);

  const filtered = useMemo(() => {
    const ordered = [...entries].sort((a, b) => b.entryDate - a.entryDate);
    return tab === "all" ? ordered : ordered.filter((e) => e.status === tab);
  }, [entries, tab]);

  const detailEntry = detailId
    ? entries.find((e) => e._id === detailId) ?? null
    : null;
  const detailLines = detailEntry
    ? lines.filter((l) => l.journalEntryId === detailEntry._id)
    : [];
  const detailTotals = detailEntry
    ? entryTotalsLocal(lines, detailEntry._id)
    : { debits: 0n, credits: 0n };

  const columns = useMemo<ColumnDef<MockJournalEntry>[]>(
    () => [
      {
        accessorKey: "entryNumber",
        header: "JE #",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.entryNumber || "—"}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "entryDate",
        header: "Date",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.entryDate, { style: "short" })}
          </span>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => (
          <span className="text-(--text-primary) line-clamp-1">
            {row.original.description}
          </span>
        ),
      },
      {
        id: "debits",
        header: "Debit total",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => {
          const t = entryTotalsLocal(lines, row.original._id);
          return (
            <span className="font-mono tabular-nums text-(--text-primary)">
              {formatMoney(t.debits, "BDT", { locale: "en-BD" })}
            </span>
          );
        },
      },
      {
        id: "credits",
        header: "Credit total",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => {
          const t = entryTotalsLocal(lines, row.original._id);
          const off = t.debits !== t.credits;
          return (
            <span
              className={
                off
                  ? "font-mono tabular-nums text-(--danger)"
                  : "font-mono tabular-nums text-(--text-primary)"
              }
            >
              {formatMoney(t.credits, "BDT", { locale: "en-BD" })}
            </span>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <JournalEntryStatusBadge status={row.original.status} />
        ),
      },
      {
        accessorKey: "sourceTable",
        header: "Source",
        cell: ({ row }) => {
          const r = row.original;
          if (!r.sourceTable) {
            return (
              <span className="text-(--text-tertiary) text-[12px]">manual</span>
            );
          }
          return (
            <span className="text-(--text-secondary) text-[12px]">
              {r.sourceTable}
            </span>
          );
        },
      },
    ],
    [lines],
  );

  function handleCreateDraft(values: JournalEntryFormValues) {
    const newId = `je_local_${Date.now()}`;
    const entryDateMs = new Date(values.entryDate).getTime();
    const newEntry: MockJournalEntry = {
      _id: newId,
      entryNumber: "",
      entryDate: entryDateMs,
      description: values.description,
      sourceTable: null,
      sourceId: null,
      status: "draft",
      postedAt: null,
      reversedByEntryId: null,
      reversalReason: null,
    };
    const newLines: MockJournalLine[] = values.lines.map((l, i) => ({
      _id: `jl_local_${Date.now()}_${i}`,
      journalEntryId: newId,
      entryDate: entryDateMs,
      accountId: l.accountId ?? "",
      debitInBaseCents: l.debitInBaseCents,
      creditInBaseCents: l.creditInBaseCents,
      currency: l.currency,
      originalAmountCents: l.originalAmountCents,
      fxRateToBaseE8: l.fxRateToBaseE8,
      description: l.description || null,
      sortOrder: i,
    }));
    setEntries((prev) => [newEntry, ...prev]);
    setLines((prev) => [...prev, ...newLines]);
    setCreateOpen(false);
    toast.success("Draft saved (mock)");
  }

  function handlePostFromForm(values: JournalEntryFormValues) {
    const newId = `je_local_${Date.now()}`;
    const entryDateMs = new Date(values.entryDate).getTime();
    const seq = entries.length + 1;
    const number = `JE-2026-${String(seq).padStart(6, "0")}`;
    const newEntry: MockJournalEntry = {
      _id: newId,
      entryNumber: number,
      entryDate: entryDateMs,
      description: values.description,
      sourceTable: null,
      sourceId: null,
      status: "posted",
      postedAt: Date.now(),
      reversedByEntryId: null,
      reversalReason: null,
    };
    const newLines: MockJournalLine[] = values.lines.map((l, i) => ({
      _id: `jl_local_${Date.now()}_${i}`,
      journalEntryId: newId,
      entryDate: entryDateMs,
      accountId: l.accountId ?? "",
      debitInBaseCents: l.debitInBaseCents,
      creditInBaseCents: l.creditInBaseCents,
      currency: l.currency,
      originalAmountCents: l.originalAmountCents,
      fxRateToBaseE8: l.fxRateToBaseE8,
      description: l.description || null,
      sortOrder: i,
    }));
    setEntries((prev) => [newEntry, ...prev]);
    setLines((prev) => [...prev, ...newLines]);
    setCreateOpen(false);
    toast.success(`${number} posted (mock)`);
  }

  function handlePostExisting(entryId: string) {
    const entry = entries.find((e) => e._id === entryId);
    if (!entry) return;
    const t = entryTotalsLocal(lines, entryId);
    if (t.debits !== t.credits) {
      toast.error("Cannot post: entry is out of balance");
      return;
    }
    const seq = entries.filter((e) => e.entryNumber).length + 1;
    const number = `JE-2026-${String(seq).padStart(6, "0")}`;
    setEntries((prev) =>
      prev.map((e) =>
        e._id === entryId
          ? {
              ...e,
              status: "posted",
              postedAt: Date.now(),
              entryNumber: number,
            }
          : e,
      ),
    );
    toast.success(`Posted as ${number}`);
  }

  function handleReverse(entryId: string, reason: string) {
    const original = entries.find((e) => e._id === entryId);
    if (!original) return;
    if (original.status !== "posted") return;
    const reversalId = `je_rev_${Date.now()}`;
    const seq = entries.filter((e) => e.entryNumber).length + 1;
    const reversalNumber = `JE-2026-${String(seq).padStart(6, "0")}`;
    const originalLines = lines.filter((l) => l.journalEntryId === entryId);
    const reversalLines: MockJournalLine[] = originalLines.map((l, i) => ({
      _id: `jl_rev_${Date.now()}_${i}`,
      journalEntryId: reversalId,
      entryDate: Date.now(),
      accountId: l.accountId,
      debitInBaseCents: l.creditInBaseCents,
      creditInBaseCents: l.debitInBaseCents,
      currency: l.currency,
      originalAmountCents: l.originalAmountCents,
      fxRateToBaseE8: l.fxRateToBaseE8,
      description: `Reversal: ${l.description ?? ""}`.trim(),
      sortOrder: i,
    }));
    const reversal: MockJournalEntry = {
      _id: reversalId,
      entryNumber: reversalNumber,
      entryDate: Date.now(),
      description: `REVERSAL of ${original.entryNumber}: ${reason}`,
      sourceTable: "journalEntries",
      sourceId: entryId,
      status: "posted",
      postedAt: Date.now(),
      reversedByEntryId: null,
      reversalReason: null,
    };
    setEntries((prev) => [
      reversal,
      ...prev.map((e) =>
        e._id === entryId
          ? {
              ...e,
              status: "reversed" as const,
              reversedByEntryId: reversalId,
              reversalReason: reason,
            }
          : e,
      ),
    ]);
    setLines((prev) => [...prev, ...reversalLines]);
    toast.success(`Reversed via ${reversalNumber}`);
  }

  function handleExportCsv(extension: "csv" | "xls") {
    // Flatten entries × lines so each spreadsheet row is one ledger line.
    type GlRow = {
      entryNumber: string;
      entryDate: string;
      status: string;
      sourceTable: string;
      sourceId: string;
      lineNumber: number;
      accountCode: string;
      accountName: string;
      debitCents: bigint;
      creditCents: bigint;
      currency: string;
      originalAmountCents: bigint;
      description: string;
    };
    const out: GlRow[] = [];
    const entriesById = new Map(entries.map((e) => [e._id, e]));
    const visibleIds = new Set(filtered.map((e) => e._id));
    for (const line of lines) {
      if (!visibleIds.has(line.journalEntryId)) continue;
      const entry = entriesById.get(line.journalEntryId);
      if (!entry) continue;
      const account = findAccount(line.accountId);
      out.push({
        entryNumber: entry.entryNumber || "(draft)",
        entryDate: formatDateForCsv(entry.entryDate),
        status: entry.status,
        sourceTable: entry.sourceTable ?? "",
        sourceId: entry.sourceId ?? "",
        lineNumber: line.sortOrder,
        accountCode: account?.code ?? "",
        accountName: account?.name ?? "",
        debitCents: line.debitInBaseCents,
        creditCents: line.creditInBaseCents,
        currency: line.currency,
        originalAmountCents: line.originalAmountCents,
        description: line.description ?? entry.description,
      });
    }
    const columns: ExportColumn<GlRow>[] = [
      { key: "entryNumber", label: "Entry #" },
      { key: "entryDate", label: "Date" },
      { key: "status", label: "Status" },
      { key: "sourceTable", label: "Source table" },
      { key: "sourceId", label: "Source id" },
      { key: "lineNumber", label: "Line #" },
      { key: "accountCode", label: "Account code" },
      { key: "accountName", label: "Account name" },
      { key: "debitCents", label: "Debit BDT" },
      { key: "creditCents", label: "Credit BDT" },
      { key: "originalAmountCents", label: "Original amount" },
      { key: "currency", label: "Currency" },
      { key: "description", label: "Description" },
    ];
    downloadCsv(
      exportFilename("journal-entries", extension),
      toCsv(out, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="General Ledger"
        description="Every state-changing event in BizOP becomes a balanced journal entry. Post, reverse, and audit them here."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New entry
            </Button>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="GL KPIs"
      >
        <KpiCard caption="Posted MTD" value={kpis.postedMtd} hint="Month to date" />
        <KpiCard caption="Drafts" value={kpis.drafts} hint="Awaiting post" />
        <KpiCard
          caption="Out-of-balance drafts"
          value={kpis.outOfBalanceDrafts}
          hint={
            kpis.outOfBalanceDrafts > 0
              ? "Need correction before posting"
              : "All clear"
          }
        />
        <KpiCard
          caption="Posted FY"
          value={kpis.postedFy}
          hint="Fiscal year to date"
        />
      </section>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          {TAB_OPTIONS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TAB_OPTIONS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-4">
            <DataTable
              data={filtered}
              columns={columns}
              getRowId={(r) => r._id}
              onRowClick={(row) => setDetailId(row.original._id)}
              emptyTitle="No journal entries"
              emptyDescription="Create one with the New entry button above."
              emptyIcon={<BookOpen className="h-5 w-5" aria-hidden />}
            />
          </TabsContent>
        ))}
      </Tabs>

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>New journal entry</SheetTitle>
            <SheetDescription>
              Save as draft to come back later, or post immediately when balanced.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <JournalEntryForm
              accounts={mockAccounts.map((a) => ({
                id: a._id,
                code: a.code,
                name: a.name,
                accountType: a.accountType,
              }))}
              onSaveDraft={handleCreateDraft}
              onPost={handlePostFromForm}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Sheet
        open={detailId !== null}
        onOpenChange={(o) => {
          if (!o) setDetailId(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {detailEntry ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono">
                    {detailEntry.entryNumber || "(unposted draft)"}
                  </span>
                  <JournalEntryStatusBadge status={detailEntry.status} />
                </SheetTitle>
                <SheetDescription>{detailEntry.description}</SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <DetailField
                    label="Date"
                    value={formatDate(detailEntry.entryDate, { style: "medium" })}
                  />
                  <DetailField
                    label="Source"
                    value={
                      detailEntry.sourceTable
                        ? `${detailEntry.sourceTable}:${detailEntry.sourceId}`
                        : "Manual"
                    }
                  />
                  <DetailField
                    label="Posted at"
                    value={
                      detailEntry.postedAt
                        ? formatDate(detailEntry.postedAt, { style: "short" })
                        : "—"
                    }
                  />
                  <DetailField
                    label="Reversed by"
                    value={
                      detailEntry.reversedByEntryId
                        ? entries.find(
                            (e) => e._id === detailEntry.reversedByEntryId,
                          )?.entryNumber ?? detailEntry.reversedByEntryId
                        : "—"
                    }
                  />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[13px] font-semibold text-(--text-primary)">
                    Lines
                  </h3>
                  <BalanceIndicator
                    debitsInBaseCents={detailTotals.debits}
                    creditsInBaseCents={detailTotals.credits}
                  />
                </div>
                <DataTable
                  data={detailLines.sort((a, b) => a.sortOrder - b.sortOrder)}
                  columns={LINE_COLUMNS}
                  getRowId={(l) => l._id}
                  emptyTitle="No lines"
                  emptyDescription="This entry has no lines yet."
                />
              </SheetBody>
              <SheetFooter>
                {detailEntry.status === "draft" ? (
                  <ConfirmPopover
                    trigger={
                      <Button>
                        <Send className="h-4 w-4" /> Post entry
                      </Button>
                    }
                    title="Post this draft?"
                    description={`Once posted, ${detailEntry.description} will be included in the General Ledger and can only be undone via a reversal.`}
                    confirmLabel="Post"
                    onConfirm={() => handlePostExisting(detailEntry._id)}
                  />
                ) : null}
                {detailEntry.status === "posted" ? (
                  <ConfirmPopover
                    trigger={
                      <Button variant="outline">
                        <RotateCcw className="h-4 w-4" /> Reverse entry
                      </Button>
                    }
                    title="Reverse this entry?"
                    description="A new mirror entry will be created with debits and credits swapped, and the original will be marked reversed."
                    confirmLabel="Reverse"
                    confirmVariant="destructive"
                    onConfirm={() =>
                      handleReverse(detailEntry._id, "Reversed from detail sheet")
                    }
                  />
                ) : null}
                {detailEntry.status === "draft" ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      toast.info("Inline editing coming soon — use New entry for now.");
                    }}
                    aria-label="Edit"
                  >
                    <Pencil className="size-4" />
                  </Button>
                ) : null}
              </SheetFooter>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function entryTotalsLocal(
  allLines: MockJournalLine[],
  entryId: string,
): { debits: bigint; credits: bigint } {
  let debits = 0n;
  let credits = 0n;
  for (const l of allLines) {
    if (l.journalEntryId !== entryId) continue;
    debits += l.debitInBaseCents;
    credits += l.creditInBaseCents;
  }
  return { debits, credits };
}

const LINE_COLUMNS: ColumnDef<MockJournalLine>[] = [
  {
    id: "account",
    header: "Account",
    cell: ({ row }) => {
      const acc = findAccount(row.original.accountId);
      if (!acc) return <span className="text-(--text-tertiary)">—</span>;
      return (
        <span className="text-[12px] text-(--text-primary)">
          <span className="font-mono mr-2 text-(--text-secondary)">{acc.code}</span>
          {acc.name}
        </span>
      );
    },
  },
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => (
      <span className="text-(--text-secondary) line-clamp-1">
        {row.original.description ?? "—"}
      </span>
    ),
  },
  {
    id: "debit",
    header: "Debit",
    meta: { numeric: true, mono: true },
    cell: ({ row }) => (
      <span className="font-mono tabular-nums">
        {row.original.debitInBaseCents > 0n
          ? formatMoney(row.original.debitInBaseCents, "BDT", { locale: "en-BD" })
          : "—"}
      </span>
    ),
  },
  {
    id: "credit",
    header: "Credit",
    meta: { numeric: true, mono: true },
    cell: ({ row }) => (
      <span className="font-mono tabular-nums">
        {row.original.creditInBaseCents > 0n
          ? formatMoney(row.original.creditInBaseCents, "BDT", { locale: "en-BD" })
          : "—"}
      </span>
    ),
  },
];

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}

